import { z } from 'zod';
import { allItemsResponse, optionalCuid, decimalString } from './common';
import { createDrawerRequest } from './project-drawers';

/**
 * Schemas for the per-project Cabinet line items.
 *
 * Path: `/projects/:projectId/cabinets`
 *
 * Snapshot semantics:
 * - When created `from-catalog`, the line item copies name/sku/dimensions/
 *   material/color from the catalog item and sets `unitPriceNet`/`currency`
 *   from the request body (price is workshop-set, not catalog-cost).
 * - `catalogItemId` is recorded while the project snapshot still matches the
 *   catalog item. If the user edits the catalog-derived snapshot,
 *   `detachFromCatalog` clears that link so it can be saved as a new catalog item.
 * - Subsequent edits to the catalog item do NOT propagate to existing lines.
 *
 * Worker access:
 * - Workers may LIST/GET cabinets only on projects they are assigned to.
 * - Workers see no `unitPriceNet`/`currency` (financials hidden).
 * - Workers may not mutate (`PROJECT_ITEMS_CREATE/UPDATE/DELETE`).
 *
 * Status locking:
 * - Mutations are forbidden when the parent project is in a terminal status
 *   (`ACCEPTED` or `REJECTED` in terminal states).
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});

const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});

const dimMm = z.number().int().min(0).max(100_000);
const optionalDimMm = z.number().int().min(0).max(100_000).optional();

const cabinetCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type CabinetCurrencyValue = z.infer<typeof cabinetCurrencyEnum>;

// ----- Cut-list ("Pe piese") construction -----
//
// PARAMETRIC cabinets are described by H/W/D + shelf/divider counts and the six
// sides are derived analytically by the pricing engine. PIECES cabinets carry an
// explicit cut list as well — it EXTENDS the parametric description rather than
// replacing it, so the material/edge fields and counts that seeded the rows stay
// on the cabinet. Only the H/W/D are re-derived, by
// `deriveCabinetDimensionsFromPieces`. Nothing is double-counted because the
// readers that spend those fields branch on this enum: pricing prices from the
// cut list alone, and BOM sums the cut list alone.
export const cabinetConstructionMode = z.enum(['PARAMETRIC', 'PIECES']);
export type CabinetConstructionModeValue = z.infer<typeof cabinetConstructionMode>;

export const cabinetPieceKind = z.enum([
  'SIDE_LEFT',
  'SIDE_RIGHT',
  'SIDE_UPPER',
  'SIDE_LOWER',
  'BACK',
  'FRONT',
  'SHELF',
  'DIVIDER',
  'CUSTOM',
]);
export type CabinetPieceKindValue = z.infer<typeof cabinetPieceKind>;

const pieceDimMm = z.number().int().min(1).max(100_000);
// How many of the two sides running along that dimension are edge-banded.
const pieceEdgeCount = z.number().int().min(0).max(2);
/**
 * How many identical boards this row stands for.
 *
 * Capped like the parametric shelf/divider counts it replaces — a seeded
 * "Poliță" row carries `quantity: politeCount` rather than one row per shelf.
 */
const pieceQuantity = z.number().int().min(1).max(10_000);
// Free text, capped so a pathological string cannot be stored. Validated by
// being parsed, not by a regex — see cabinet-dimension-formula.ts.
const pieceFormula = z.string().trim().max(200).nullable().optional();

export const MAX_CABINET_PIECES = 100;

/** One cut-list row on the wire (create / update payload). */
export const cabinetPieceInput = z.object({
  kind: cabinetPieceKind,
  label: z.string().trim().min(1).max(120),
  lengthMm: pieceDimMm,
  widthMm: pieceDimMm,
  materialId: z.string().cuid().nullable().optional(),
  edgeId: z.string().cuid().nullable().optional(),
  edgeCountLength: pieceEdgeCount.default(0),
  edgeCountWidth: pieceEdgeCount.default(0),
  /**
   * Two boards laminated together — the per-piece analogue of the parametric
   * Cx2 / Px2 / Ix2 flags. Doubles the material cost only; a doubled panel still
   * has four edges, so banding is unchanged.
   */
  doubled: z.boolean().default(false),
  /**
   * Identical boards described by this one row. Multiplies BOTH the material and
   * the banding — unlike `doubled`, which laminates one board and so leaves the
   * edges alone. Defaults to 1, so payloads written before the column existed
   * still parse and price exactly as they did.
   */
  quantity: pieceQuantity.default(1),
  /**
   * Markup for this row's board — per ROW, so two boards of the same PAL can be
   * marked up differently inside one cut list.
   *
   * Optional rather than defaulted: left out, the server copies the board's
   * `Material.coefficient`, and a default here would make "not sent" look
   * exactly like "the user typed 2.50".
   */
  coefficient: nonNegativeDecimal.optional(),
  /**
   * The spreadsheet-style formula behind each dimension, when there is one
   * (). The millimetres above stay the source of truth for pricing and
   * BOM; these only say where they came from, so the value can follow the
   * cabinet when its Custom dimensions change. Null means the number was typed.
   */
  lengthFormula: pieceFormula,
  widthFormula: pieceFormula,
});
export type CabinetPieceInput = z.infer<typeof cabinetPieceInput>;

/** A persisted cut-list row as returned by the API. */
export const cabinetPieceListItem = cabinetPieceInput.extend({
  id: z.string(),
  cabinetId: z.string(),
  /** Always present once stored — resolved from the material when not sent. */
  coefficient: decimalString,
  materialId: z.string().nullable(),
  edgeId: z.string().nullable(),
  orderIndex: z.number().int(),
});
export type CabinetPieceListItem = z.infer<typeof cabinetPieceListItem>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const cabinetListItem = z.object({
  id: z.string(),
  projectId: z.string(),
  roomId: z.string().cuid().nullable(),
  areaId: z.string().cuid().nullable(),
  catalogItemId: z.string().nullable(),
  name: z.string(),
  sku: z.string().nullable(),
  widthMm: z.number().int(),
  heightMm: z.number().int(),
  depthMm: z.number().int(),
  material: z.string().nullable(),
  frontMaterial: z.string().nullable(),
  backMaterial: z.string().nullable(),
  /**
   * The board(s) as the item tables show them, by the shared rule the quote PDF
   * uses (`formatMaterials`): the saved label when there is one, else the
   * catalog name. Filled by the list endpoints, which load the names; absent
   * on single-item responses. '' when the item carries no material.
   */
  materialDisplay: z.string().optional(),
  color: z.string().nullable(),
  edging: z.string().nullable(),
  intermediarCount: z.number().int().nullable(),
  politeCount: z.number().int().nullable(),
  joinType: z.string().nullable(),
  intermediarDoubled: z.boolean(),
  carcassDoubled: z.boolean(),
  carcassExteriorFront: z.boolean(),
  politeDoubled: z.boolean(),
  quantity: decimalString,
  coefficient: decimalString,
  carcassCoefficient: decimalString,
  frontCoefficient: decimalString,
  backCoefficient: decimalString,
  // Financial fields are absent for callers without PROJECTS_READ_FINANCIALS.
  unitPriceNet: decimalString.optional(),
  /**
   * Unit price WITHOUT the markup: MANUAL rows repeat `unitPriceNet`; AUTO rows
   * report the engine's catalog-cost sum (`subtotalBaseNet`), absent until a
   * breakdown stored before that field existed is recomputed. Absent when
   * finances are hidden.
   */
  unitPriceBaseNet: decimalString.optional(),
  /**
   * Unit price WITH the markup: `unitPriceNet` times the row coefficient. On
   * AUTO rows the markups already live inside `unitPriceNet`, so the two agree
   * there by construction. Absent when finances are hidden.
   */
  unitPriceFinalNet: decimalString.optional(),
  currency: cabinetCurrencyEnum.optional(),
  // Server-computed body ("Total corp") total: quantity * coefficient * unitPriceNet.
  // Absent when finances hidden.
  totalNet: decimalString.optional(),
  // Server-computed rollup of the cabinet's drawers (Σ of each drawer's
  // quantity * coefficient * unitPriceNet). '0.00' when the cabinet has no
  // drawers; absent when finances hidden.
  drawersTotalNet: decimalString.optional(),
  // Server-computed rollup of the cabinet's own hardware ("feronerie"), already
  // scaled by the cabinet quantity. Only MANUAL cabinets carry a figure here: an
  // AUTO cabinet's hardware is priced into its unitPriceNet, so counting it
  // again would double it — the same rule recomputeProjectTotals applies.
  // '0.00' when there is nothing to add; absent when finances hidden.
  hardwareTotalNet: decimalString.optional(),
  // Server-computed combined total shown on the cabinet line: totalNet
  // ("Total corp") + drawersTotalNet ("sertare") + hardwareTotalNet
  // ("feronerie"). This is the cabinet's whole contribution to its room and to
  // the project total — the three parts are broken out above so a reader can
  // see where it comes from. Absent when finances hidden.
  totalNetWithExtras: decimalString.optional(),
  // Pricing engine fields (PRICING_ENGINE_PLAN E.3, PR #11f).
  pricingMode: z.enum(['MANUAL', 'AUTO']),
  materialId: z.string().nullable(),
  edgeId: z.string().nullable(),
  frontMaterialId: z.string().nullable(),
  frontEdgeId: z.string().nullable(),
  backMaterialId: z.string().nullable(),
  backEdgeId: z.string().nullable(),
  backHasEdge: z.boolean(),
  joinTypeKind: z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']).nullable(),
  // On the list item (not just details) so the edit dialog can pick the right
  // form before the detail query resolves.
  constructionMode: cabinetConstructionMode,
  computedBreakdown: z.unknown().nullable().optional(),
  pricingError: z.string().optional(),
  orderIndex: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CabinetListItem = z.infer<typeof cabinetListItem>;

// Re-import for cabinetDetails — avoid circular dependency by using lazy evaluation
const lazyDrawerListItem = z.lazy(() =>
  z.object({
    id: z.string().cuid(),
    cabinetId: z.string().cuid(),
    catalogItemId: z.string().cuid().nullable(),
    name: z.string().nullable(),
    heightMm: z.number().int().nullable(),
    widthMm: z.number().int().nullable(),
    depthMm: z.number().int().nullable(),
    carcassMaterial: z.string().nullable(),
    frontMaterial: z.string().nullable(),
    drawerModel: z.string().nullable(),
    drawerType: z.string().nullable(),
    slideModel: z.string().nullable(),
    hBox: z.string().nullable(),
    color: z.string().nullable(),
    quantity: decimalString,
    coefficient: decimalString,
    unitPriceNet: decimalString.optional(),
    currency: z.enum(['RON', 'EUR', 'USD']).optional(),
    totalNet: decimalString.optional(),
    orderIndex: z.number().int(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
);

const lazyHardwareListItem = z.lazy(() =>
  z.object({
    id: z.string(),
    projectId: z.string(),
    roomId: z.string().cuid().nullable(),
    areaId: z.string().cuid().nullable(),
    cabinetId: z.string().nullable(),
    catalogItemId: z.string().nullable(),
    hardwareCatalogId: z.string().nullable(),
    name: z.string(),
    sku: z.string().nullable(),
    finish: z.string().nullable(),
    material: z.string().nullable(),
    quantity: decimalString,
    lengthM: decimalString.optional(),
    coefficient: decimalString,
    unitPriceNet: decimalString.optional(),
    currency: z.enum(['RON', 'EUR', 'USD']).optional(),
    totalNet: decimalString.optional(),
    orderIndex: z.number().int(),
    notes: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  }),
);

const createCabinetHardwareItem = z.object({
  hardwareCatalogId: z.string().cuid(),
  quantity: positiveDecimal.default('1'),
  lengthM: nonNegativeDecimal.optional(),
  coefficient: positiveDecimal.default('1.00'),
});
export type CreateCabinetHardwareItem = z.infer<typeof createCabinetHardwareItem>;

export const cabinetDetails = cabinetListItem.extend({
  drawers: z.array(lazyDrawerListItem),
  hardware: z.array(lazyHardwareListItem),
  // Always present; empty for PARAMETRIC cabinets.
  pieces: z.array(cabinetPieceListItem),
});
export type CabinetDetails = z.infer<typeof cabinetDetails>;

export const listCabinetsResponse = allItemsResponse(cabinetListItem);
export type ListCabinetsResponse = z.infer<typeof listCabinetsResponse>;

// ----- Manual create (no catalog reference) -----
export const createCabinetRequest = z.object({
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  name: z.string().trim().min(1).max(200),
  sku: optionalTrim(50),
  widthMm: dimMm,
  heightMm: dimMm,
  depthMm: dimMm,
  material: optionalTrim(100),
  frontMaterial: optionalTrim(100),
  backMaterial: optionalTrim(100),
  color: optionalTrim(50),
  edging: optionalTrim(100),
  intermediarCount: z.number().int().min(0).optional(),
  politeCount: z.number().int().min(0).optional(),
  joinType: optionalTrim(100),
  intermediarDoubled: z.boolean().default(false),
  carcassDoubled: z.boolean().default(false),
  carcassExteriorFront: z.boolean().default(false),
  politeDoubled: z.boolean().default(false),
  quantity: positiveDecimal.default('1'),
  coefficient: nonNegativeDecimal.default('1.00'),
  /**
   * Markup for THIS use of each board, seeded from `Material.coefficient` and
   * kept on the cabinet. There is no cabinet-wide coefficient any more — one
   * cabinet can carry a 2.5 carcass and a 1.0 painted front — so `coefficient`
   * above is pinned to 1 on AUTO rows and only means anything on MANUAL ones.
   * The carcass value also prices the shelves and dividers cut from that board.
   */
  carcassCoefficient: nonNegativeDecimal.optional(),
  frontCoefficient: nonNegativeDecimal.optional(),
  backCoefficient: nonNegativeDecimal.optional(),
  unitPriceNet: nonNegativeDecimal.default('0.00'),
  currency: cabinetCurrencyEnum.default('RON'),
  notes: optionalTrim(2000),
  // Pricing engine fields (PRICING_ENGINE_PLAN E.3, PR #11f). Send
  // `pricingMode: 'AUTO'` together with materialId/edgeId/joinTypeKind to have
  // the server compute `unitPriceNet`. On AUTO failure the row is created in
  // MANUAL mode with the user-supplied price; the server returns
  // `pricingError`.
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  materialId: z.string().cuid().optional().nullable(),
  edgeId: z.string().cuid().optional().nullable(),
  frontMaterialId: z.string().cuid().optional().nullable(),
  frontEdgeId: z.string().cuid().optional().nullable(),
  backMaterialId: z.string().cuid().optional().nullable(),
  backEdgeId: z.string().cuid().optional().nullable(),
  backHasEdge: z.boolean().optional(),
  joinTypeKind: z
    .enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE'])
    .optional()
    .nullable(),
  hardwareItems: z.array(createCabinetHardwareItem).max(50).optional(),
  drawers: z.array(createDrawerRequest).max(50).optional(),
  // Cut-list construction. When `constructionMode: 'PIECES'`, the server derives
  // widthMm/heightMm/depthMm from `pieces`; every other parametric field is
  // stored exactly as sent, because the cut list extends the Custom cabinet
  // rather than replacing it.
  constructionMode: cabinetConstructionMode.optional(),
  pieces: z.array(cabinetPieceInput).max(MAX_CABINET_PIECES).optional(),
});
export type CreateCabinetRequest = z.infer<typeof createCabinetRequest>;

// ----- Create from a CatalogItem (snapshot) -----
//
// Required fields the catalog item may not have are surfaced via `overrides`.
// Unit price MUST be set explicitly here — catalog stores cost, line items
// store sell price, and the API does not assume a markup.
export const createCabinetFromCatalogRequest = z.object({
  catalogItemId: z.string().min(1),
  detachFromCatalog: z.boolean().optional(),
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  unitPriceNet: nonNegativeDecimal,
  currency: cabinetCurrencyEnum.optional(),
  quantity: positiveDecimal.default('1'),
  coefficient: nonNegativeDecimal.default('1.00'),
  /**
   * Markup for THIS use of each board, seeded from `Material.coefficient` and
   * kept on the cabinet. There is no cabinet-wide coefficient any more — one
   * cabinet can carry a 2.5 carcass and a 1.0 painted front — so `coefficient`
   * above is pinned to 1 on AUTO rows and only means anything on MANUAL ones.
   * The carcass value also prices the shelves and dividers cut from that board.
   */
  carcassCoefficient: nonNegativeDecimal.optional(),
  frontCoefficient: nonNegativeDecimal.optional(),
  backCoefficient: nonNegativeDecimal.optional(),
  notes: optionalTrim(2000),
  overrides: z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      sku: optionalTrim(50),
      widthMm: optionalDimMm,
      heightMm: optionalDimMm,
      depthMm: optionalDimMm,
      material: optionalTrim(100),
      frontMaterial: optionalTrim(100),
      backMaterial: optionalTrim(100),
      color: optionalTrim(50),
      edging: optionalTrim(100),
      intermediarCount: z.number().int().min(0).optional(),
      politeCount: z.number().int().min(0).optional(),
      joinType: optionalTrim(100),
      intermediarDoubled: z.boolean().optional(),
      carcassDoubled: z.boolean().optional(),
      carcassExteriorFront: z.boolean().optional(),
      politeDoubled: z.boolean().optional(),
      pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
      materialId: z.string().cuid().optional().nullable(),
      edgeId: z.string().cuid().optional().nullable(),
      frontMaterialId: z.string().cuid().optional().nullable(),
      frontEdgeId: z.string().cuid().optional().nullable(),
      backMaterialId: z.string().cuid().optional().nullable(),
      backEdgeId: z.string().cuid().optional().nullable(),
      backHasEdge: z.boolean().optional(),
      joinTypeKind: z
        .enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE'])
        .optional()
        .nullable(),
      hardwareItems: z.array(createCabinetHardwareItem).max(50).optional(),
      drawers: z.array(createDrawerRequest).max(50).optional(),
      constructionMode: cabinetConstructionMode.optional(),
      pieces: z.array(cabinetPieceInput).max(MAX_CABINET_PIECES).optional(),
    })
    .optional(),
});
export type CreateCabinetFromCatalogRequest = z.infer<typeof createCabinetFromCatalogRequest>;

// ----- Update (partial) -----
//
// `catalogItemId` is intentionally not directly patchable. `detachFromCatalog`
// can clear the link when a catalog-derived snapshot diverges from its source.
// `projectId` is taken from the URL.
export const updateCabinetRequest = z
  .object({
    detachFromCatalog: z.boolean(),
    roomId: z.string().cuid().nullable(),
    areaId: z.string().cuid().nullable(),
    name: z.string().trim().min(1).max(200),
    sku: optionalTrim(50),
    widthMm: dimMm,
    heightMm: dimMm,
    depthMm: dimMm,
    material: optionalTrim(100),
    frontMaterial: optionalTrim(100),
    backMaterial: optionalTrim(100),
    color: optionalTrim(50),
    edging: optionalTrim(100),
    intermediarCount: z.number().int().min(0).nullable(),
    politeCount: z.number().int().min(0).nullable(),
    joinType: optionalTrim(100),
    intermediarDoubled: z.boolean(),
    carcassDoubled: z.boolean(),
    carcassExteriorFront: z.boolean(),
    politeDoubled: z.boolean(),
    quantity: positiveDecimal,
    coefficient: nonNegativeDecimal,
    carcassCoefficient: nonNegativeDecimal,
    frontCoefficient: nonNegativeDecimal,
    backCoefficient: nonNegativeDecimal,
    unitPriceNet: nonNegativeDecimal,
    currency: cabinetCurrencyEnum,
    orderIndex: z.number().int().min(0),
    notes: optionalTrim(2000),
    // Pricing engine fields (PR #11f).
    pricingMode: z.enum(['MANUAL', 'AUTO']),
    materialId: z.string().cuid().nullable(),
    edgeId: z.string().cuid().nullable(),
    frontMaterialId: z.string().cuid().nullable(),
    frontEdgeId: z.string().cuid().nullable(),
    backMaterialId: z.string().cuid().nullable(),
    backEdgeId: z.string().cuid().nullable(),
    backHasEdge: z.boolean(),
    joinTypeKind: z.enum(['EUROSCREW', 'DEMONTABIL', 'CLAMEX', 'MITER_45', 'NONE']).nullable(),
    hardwareItems: z.array(createCabinetHardwareItem).max(50),
    constructionMode: cabinetConstructionMode,
    // Replaces the cut list wholesale. Omit to leave the existing rows untouched;
    // send `[]` to clear them.
    pieces: z.array(cabinetPieceInput).max(MAX_CABINET_PIECES),
  })
  .partial();
export type UpdateCabinetRequest = z.infer<typeof updateCabinetRequest>;
